/**
 * Area-Based Calculation Utilities
 * 
 * Provides functions for calculating demographics, market metrics, and comparisons
 * for geographic locations that don't have their own enrollment data.
 */

import { calculateEnrollment } from './schoolUtils';

/**
 * Calculate demographics for the area around a location based on nearby schools
 * @param {Object} params - Parameters object
 * @param {Object} params.nearbySchools - Nearby schools data structure
 * @param {Array} params.schoolsEnrollmentData - Enrollment data for all schools
 * @param {Array} params.selectedGrades - Grade levels to include in calculations
 * @param {Number} params.selectedDriveTime - Drive time in minutes
 * @returns {Object} Area demographic statistics
 */
export const calculateAreaDemographics = ({
  nearbySchools,
  schoolsEnrollmentData,
  selectedGrades,
  selectedDriveTime
}) => {
  if (!nearbySchools || !schoolsEnrollmentData || !selectedGrades) {
    return {
      demographics: {},
      totalEnrollment: 0,
      largestGroup: null,
      largestGroupCount: 0,
      largestGroupPercentage: 0,
      schoolCount: 0
    };
  }

  const relevantSchools = nearbySchools?.current?.[selectedDriveTime] || [];
  
  const demographics = {};
  let totalEnrollment = 0;
  let schoolsWithData = 0;

  relevantSchools.forEach(school => {
    const enrollmentData = schoolsEnrollmentData.find(d => d.uuid === school.uuid);
    if (enrollmentData?.race_data?.current) {
      let hasDataForThisSchool = false;
      
      selectedGrades.forEach(grade => {
        const gradeRaceData = enrollmentData.race_data.current[grade];
        if (gradeRaceData && Object.keys(gradeRaceData).length > 0) {
          hasDataForThisSchool = true;
          Object.entries(gradeRaceData).forEach(([race, count]) => {
            if (typeof count === 'number' && count > 0) {
              demographics[race] = (demographics[race] || 0) + count;
              totalEnrollment += count;
            }
          });
        }
      });
      
      if (hasDataForThisSchool) {
        schoolsWithData++;
      }
    }
  });

  // Find largest demographic group
  const sortedGroups = Object.entries(demographics)
    .sort(([,a], [,b]) => b - a);
  
  const largestGroup = sortedGroups[0];
  
  // Map internal race codes to display names
  const getDisplayName = (raceCode) => {
    const raceMap = {
      'white': 'White',
      'black': 'Black or African American',
      'hispanic': 'Hispanic or Latino',
      'asian': 'Asian',
      'american_indian': 'American Indian or Alaska Native',
      'pacific_islander': 'Native Hawaiian or Pacific Islander',
      'two_or_more': 'Two or More Races'
    };
    return raceMap[raceCode] || raceCode;
  };

  return {
    demographics,
    totalEnrollment,
    largestGroup: largestGroup ? getDisplayName(largestGroup[0]) : null,
    largestGroupCount: largestGroup ? largestGroup[1] : 0,
    largestGroupPercentage: totalEnrollment > 0 ? (largestGroup?.[1] / totalEnrollment) * 100 : 0,
    schoolCount: relevantSchools.length,
    schoolsWithDemographicData: schoolsWithData
  };
};

/**
 * Calculate average enrollment change for schools in an area
 * @param {Array} schools - Array of nearby schools
 * @param {Array} schoolsEnrollmentData - Enrollment data for all schools
 * @param {Array} selectedGrades - Grade levels to include
 * @returns {Object} Enrollment change statistics
 */
export const calculateAverageEnrollmentChange = (schools, schoolsEnrollmentData, selectedGrades) => {
  if (!schools || !schoolsEnrollmentData || !selectedGrades) {
    return {
      averageChange: 0,
      schoolsWithData: 0,
      totalSchools: 0,
      changes: []
    };
  }

  const validChanges = [];
  let schoolsWithData = 0;

  schools.forEach(school => {
    const enrollmentData = schoolsEnrollmentData.find(d => d.uuid === school.uuid);
    
    if (enrollmentData?.enrollment_by_grade) {
      const currentEnrollment = calculateEnrollment(
        enrollmentData.enrollment_by_grade.current || {}, 
        selectedGrades
      );
      const pastEnrollment = calculateEnrollment(
        enrollmentData.enrollment_by_grade.comparison || {}, 
        selectedGrades
      );

      if (currentEnrollment > 0 && pastEnrollment > 0) {
        const change = ((currentEnrollment - pastEnrollment) / pastEnrollment) * 100;
        validChanges.push({
          schoolName: school.name,
          change,
          currentEnrollment,
          pastEnrollment
        });
        schoolsWithData++;
      }
    }
  });

  const averageChange = validChanges.length > 0 
    ? validChanges.reduce((sum, item) => sum + item.change, 0) / validChanges.length
    : 0;

  return {
    averageChange,
    schoolsWithData,
    totalSchools: schools.length,
    changes: validChanges,
    hasValidData: validChanges.length > 0
  };
};

/**
 * Calculate population-based market potential for a location
 * @param {Object} params - Parameters object
 * @param {Object} params.esriData - ESRI demographic data
 * @param {Array} params.selectedGrades - Grade levels for analysis
 * @param {Number} params.selectedDriveTime - Drive time in minutes
 * @returns {Object} Population market statistics
 */
export const calculatePopulationMarketPotential = ({
  esriData,
  selectedGrades,
  selectedDriveTime
}) => {
  if (!esriData || !selectedGrades || !selectedDriveTime) {
    return {
      totalPopulation: 0,
      gradePopulation: {},
      hasValidData: false
    };
  }

  // Get population data for the specified drive time
  const driveTimeData = esriData[selectedDriveTime];
  if (!driveTimeData) {
    return {
      totalPopulation: 0,
      gradePopulation: {},
      hasValidData: false
    };
  }

  const gradePopulation = {};
  let totalPopulation = 0;

  // Map grade names to ESRI field names
  const gradeFieldMap = {
    'Pre-Kindergarten': 'pre_k',
    'Kindergarten': 'kindergarten',
    '1': 'grade_1',
    '2': 'grade_2',
    '3': 'grade_3',
    '4': 'grade_4',
    '5': 'grade_5',
    '6': 'grade_6',
    '7': 'grade_7',
    '8': 'grade_8',
    '9': 'grade_9',
    '10': 'grade_10',
    '11': 'grade_11',
    '12': 'grade_12'
  };

  selectedGrades.forEach(grade => {
    const fieldName = gradeFieldMap[grade];
    if (fieldName && driveTimeData.current && typeof driveTimeData.current[fieldName] === 'number') {
      const population = driveTimeData.current[fieldName];
      gradePopulation[grade] = population;
      totalPopulation += population;
    }
  });

  return {
    totalPopulation,
    gradePopulation,
    hasValidData: totalPopulation > 0,
    driveTime: selectedDriveTime
  };
};

/**
 * Filter schools that have enrollment data for meaningful comparisons
 * @param {Array} schools - Array of nearby schools
 * @param {Array} schoolsEnrollmentData - Enrollment data for all schools
 * @returns {Array} Schools with valid enrollment data
 */
export const filterSchoolsWithEnrollmentData = (schools, schoolsEnrollmentData) => {
  if (!schools || !schoolsEnrollmentData) {
    return [];
  }

  return schools.filter(school => {
    const enrollmentData = schoolsEnrollmentData.find(d => d.uuid === school.uuid);
    return enrollmentData?.enrollment_by_grade?.current && 
           Object.keys(enrollmentData.enrollment_by_grade.current).length > 0;
  });
};

/**
 * Calculate area market share statistics for nearby schools (excluding the selected location)
 * @param {Object} params - Parameters object
 * @param {Array} params.nearbySchools - Array of nearby school objects
 * @param {Array} params.schoolsEnrollmentData - Enrollment data array
 * @param {Array} params.selectedGrades - Selected grades for analysis
 * @param {Number} params.selectedDriveTime - Drive time in minutes
 * @param {Array} params.governanceFilter - Governance filter array (e.g., ['Charter', 'District', 'Private'])
 * @returns {Object} Area market statistics
 */
export const calculateAreaMarketStatistics = ({
  nearbySchools,
  schoolsEnrollmentData,
  selectedGrades,
  selectedDriveTime,
  governanceFilter = ['Charter', 'District', 'Private']
}) => {
  const relevantSchools = nearbySchools?.current?.[selectedDriveTime] || [];
  
  // Filter by governance if needed (governanceFilter is now an array)
  const filteredSchools = (!governanceFilter || governanceFilter.length === 0 || governanceFilter.length === 3)
    ? relevantSchools
    : relevantSchools.filter(school => {
        const isCharter = school.charter === 'Yes' || school.is_charter === true;
        const isPrivate = school.is_private === true;
        
        let matches = false;
        
        if (governanceFilter.includes('Charter')) {
          if (isCharter && !isPrivate) matches = true;
        }
        if (governanceFilter.includes('District')) {
          if (!isCharter && !isPrivate) matches = true;
        }
        if (governanceFilter.includes('Private')) {
          if (isPrivate) matches = true;
        }
        
        return matches;
      });

  // Calculate total area enrollment
  const areaEnrollment = filteredSchools.reduce((total, school) => {
    const enrollmentData = schoolsEnrollmentData.find(d => d.uuid === school.uuid);
    const schoolEnrollment = calculateEnrollment(
      enrollmentData?.enrollment_by_grade?.current || {},
      selectedGrades
    );
    return total + schoolEnrollment;
  }, 0);

  // Calculate demographic composition
  const areaDemographics = calculateAreaDemographics({
    nearbySchools: { current: { [selectedDriveTime]: filteredSchools } },
    schoolsEnrollmentData,
    selectedGrades,
    selectedDriveTime
  });

  // Calculate enrollment trends
  const enrollmentTrends = calculateAverageEnrollmentChange(
    filteredSchools,
    schoolsEnrollmentData,
    selectedGrades
  );

  return {
    totalEnrollment: areaEnrollment,
    schoolCount: filteredSchools.length,
    demographics: areaDemographics,
    enrollmentTrends,
    governanceFilter,
    driveTime: selectedDriveTime,
    hasValidData: areaEnrollment > 0 || areaDemographics.totalEnrollment > 0
  };
};

/**
 * Generate area analysis summary text for location-only scenarios
 * @param {Object} areaStats - Area statistics from calculateAreaMarketStatistics
 * @param {String} locationName - Name of the location
 * @returns {String} Formatted summary text
 */
export const generateAreaAnalysisSummary = (areaStats, locationName) => {
  if (!areaStats.hasValidData) {
    return `Limited data available for the area around ${locationName}.`;
  }

  const { demographics, enrollmentTrends, schoolCount, totalEnrollment } = areaStats;
  
  let summary = `The area around ${locationName} is served by ${schoolCount} schools`;
  
  if (totalEnrollment > 0) {
    summary += ` with a total enrollment of ${totalEnrollment.toLocaleString()} students`;
  }
  
  if (demographics.largestGroup && demographics.largestGroupPercentage > 0) {
    summary += `. The area primarily serves ${demographics.largestGroup} students (${Math.round(demographics.largestGroupPercentage)}%)`;
  }
  
  if (enrollmentTrends.hasValidData) {
    const trend = enrollmentTrends.averageChange > 0 ? 'growth' : 'decline';
    summary += `, with an average enrollment ${trend} of ${Math.round(Math.abs(enrollmentTrends.averageChange))}% since 2019`;
  }
  
  summary += '.';
  
  return summary;
};
